import React, { useState, useEffect,useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../context/AuthContext";
import { API_URL } from '../pages/config'; 

const HistoryPanel = ({ 
  isDarkMode, 
  isOpen, 
  onClose, 
  onSelectSession, 
  onClearHistory,
  currentSessionId,
  refreshHistory,
  onDeleteSession
}) => {
  const navigate = useNavigate();
  const { currentUser, logout ,idToken,isAnonymous } = useAuth();  
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });

  // Fetch chat history when the panel opens
  useEffect(() => {
    if (isOpen && !isAnonymous) {
      fetchChatHistory();
    } else if (isAnonymous) {
      setChatHistory([]);
      setIsLoading(false);
      setError(null);
    }
  }, [isOpen, isAnonymous, refreshHistory]);

  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        event.target instanceof Node // Ensure target is a DOM node
      ) {
        console.debug('Clicked outside history panel, closing'); // Debug log
        onClose();
      }
    };
  
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
  
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const fetchChatHistory = async () => {
    const timestamp = new Date().toISOString();
    //console.log(`[${timestamp}] Starting fetchChatHistory, idToken present: ${!!idToken}, API_URL: ${process.env.REACT_APP_API_URL || 'undefined'}`);
    //console.log(`[${timestamp}] Current chatHistory state (before fetch):`, chatHistory);

    setIsLoading(true);
    setError(null);

    try {
      if (!idToken) {
        console.error(`[${timestamp}] No idToken provided for chat history request`);
        throw new Error('Authentication token not found');
      }

      // Obfuscate idToken for logging (show first 10 and last 10 chars)
      //console.log(`[${timestamp}] Using idToken: ${idToken}`);

      const apiUrl = `${API_URL}/api/chat-history`;
      //console.log(`[${timestamp}] Sending request to: ${apiUrl}`);

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      // Log response status and headers
      //console.log(`[${timestamp}] Received response, status: ${response.status}, ok: ${response.ok}`);
      const headers = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      //console.log(`[${timestamp}] Response headers:`, headers);

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
          console.error(`[${timestamp}] API error response:`, errorData);
        } catch (jsonError) {
          console.error(`[${timestamp}] Failed to parse error response:`, jsonError.message);
        }
        throw new Error(errorData.detail || `Failed to load chat history (status: ${response.status})`);
      }

      const data = await response.json();
      //console.log(`[${timestamp}] Chat history data received:`, data);
      //onsole.log(`[${timestamp}] History list length: ${data.history ? data.history.length : 'undefined'}`);
      if (data.history) {
        console.log(`[${timestamp}] History list contents:`, data.history.map(item => ({
          id: item.id,
          title: item.title,
          last_updated: item.last_updated
        })));
      }

      setChatHistory(data.history || []);
      //console.log(`[${timestamp}] Updated chatHistory state (after fetch):`, data.history || []);

    } catch (err) {
      console.error(`[${timestamp}] Error fetching chat history:`, {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setError(err.message);
    } finally {
      //console.log(`[${timestamp}] fetchChatHistory completed, isLoading: false, error: ${error || 'none'}`);
      setIsLoading(false);
    }
  };

  const getRelativeTime = (timestamp) => {
    try {
      if (!timestamp) return 'Unknown';
      const now = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(), new Date().getUTCHours(), new Date().getUTCMinutes(), new Date().getUTCSeconds()));
      const date = new Date(timestamp.endsWith('Z') ? timestamp : `${timestamp}Z`);
      const diffMs = now - date;
      const diffMin = Math.round(diffMs / 60000);
      const diffHours = Math.round(diffMin / 60);
      const diffDays = Math.round(diffHours / 24);
      const diffWeeks = Math.round(diffDays / 7);
      const diffMonths = Math.round(diffDays / 30);
      
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin} min ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
      return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    } catch (e) {
      console.error('Error calculating relative time:', e);
      return 'Unknown';
    }
  };

  const getTimeBuckets = () => {
    const buckets = {
      Today: [],
      Yesterday: [],
      'This Week': [],
      'This Month': [],
      'Long Ago': []
    };
  
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const yesterday = new Date(today);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setUTCDate(weekAgo.getUTCDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setUTCDate(monthAgo.getUTCDate() - 30);
  
    chatHistory.forEach(session => {
      const date = new Date(session.last_updated);
      if (date >= today) {
        buckets.Today.push(session);
      } else if (date >= yesterday) {
        buckets.Yesterday.push(session);
      } else if (date >= weekAgo) {
        buckets['This Week'].push(session);
      } else if (date >= monthAgo) {
        buckets['This Month'].push(session);
      } else {
        buckets['Long Ago'].push(session);
      }
    });
  
    Object.keys(buckets).forEach(key => {
      buckets[key].sort((a, b) => new Date(b.last_updated) - new Date(a.last_updated));
    });
  
    return buckets;
  };

  const timeBuckets = getTimeBuckets();

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation(); // Prevent triggering the parent onClick
    try {
      const response = await fetch(`${API_URL}/api/chat-history/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete chat session');
      }

      // Remove the deleted session from the UI
      await fetchChatHistory(); // Refresh history from backend
      onDeleteSession(sessionId); // Notify MainComponent
      showToast('Chat session deleted', 'success');
    } catch (err) {
      console.error('Error deleting chat session:', err);
      setError(err.message);
      showToast('Failed to delete session', 'error');
    }
  };

  const clearAllHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat-history`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to clear chat history');
      }

      setChatHistory([]);
      onClearHistory();
      showToast('All chat history cleared', 'success');
    } catch (err) {
      console.error('Error clearing chat history:', err);
      setError(err.message);
      showToast('Failed to clear history', 'error');
    }
  };

  // Show toast notification
  const showToast = (message, type) => {
    setToast({ isVisible: true, message, type });
    setTimeout(() => setToast({ isVisible: false, message: '', type }), 3000);
  };

  // Extract the first message from each chat session for preview
  const getSessionPreview = (session) => {
    if (!session.messages || session.messages.length === 0) {
      return 'Empty conversation';
    }
    
    const firstMessage = session.messages.find(msg => msg.role === 'user') || session.messages[0];
    const content = firstMessage.content || '';
    
    // Truncate the message if it's too long
    return content.length > 30 ? content.substring(0, 27) + '...' : content;
  };

  // Format the timestamp to a readable date/time
  const formatTimestamp = (timestamp) => {
    try {
      if (!timestamp) return '';
      
      const date = new Date(timestamp.endsWith('Z') ? timestamp : `${timestamp}Z`);
      
      if (isNaN(date.getTime())) return '';
      
      const today = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()));
      const yesterday = new Date(today);
      yesterday.setUTCDate(yesterday.getUTCDate() - 1);
      
      if (date >= today) {
        return 'Today, ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'UTC' });
      } else if (date >= yesterday) {
        return 'Yesterday, ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'UTC' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      }
    } catch (e) {
      console.error('Error formatting timestamp:', e);
      return '';
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
      showToast('Failed to log out', 'error');
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
    }
  }, [currentUser, navigate]);

  return (
    <motion.div 
      ref={panelRef}
      className={`fixed inset-y-0 left-0 w-64 z-50 shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'}`}
      initial={{ x: -320 }}
      animate={{ x: isOpen ? 0 : -320 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className="flex flex-col h-full">
        {/* History Panel Header */}
        <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
          <h2 className="font-semibold text-lg">Chat History</h2>
          <button 
            onClick={onClose}
            className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        
        {/* Anonymous User Message */}
        {isAnonymous && !isLoading && (
          <div className={`text-center p-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Sign in to save and view chat history
          </div>
        )}
        
        {/* Create New Chat Button */}
        {!isAnonymous && (
          <button 
            onClick={() => onSelectSession(null)}
            className={`mx-3 mt-3 mb-1 flex items-center gap-2 p-2 rounded ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M10 3a1 1 0 00-1 1v5H4a1 1 0 100 2h5v5a1 1 0 102 0v-5h5a1 1 0 100-2h-5V4a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            New Chat
          </button>
        )}
        
        {/* Clear All History Button */}
        {!isAnonymous && chatHistory.length > 0 && (
          <button 
            onClick={clearAllHistory}
            className={`mx-3 mb-3 flex items-center gap-2 p-2 rounded ${isDarkMode ? 'bg-red-600/20 hover:bg-red-600/30 text-red-300' : 'bg-red-50 hover:bg-red-100 text-red-600'}`}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Clear All History
          </button>
        )}
        
      {/* Chat History List */}
      {!isAnonymous && (
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${isDarkMode ? 'border-blue-400' : 'border-blue-500'}`}></div>
            </div>
          ) : error ? (
            <div className={`text-center p-4 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
              {error}
            </div>
          ) : Object.values(timeBuckets).every(bucket => bucket.length === 0) ? (
            <div className={`text-center p-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No chat history available
            </div>
          ) : (
            <div>
              {Object.entries(timeBuckets).map(([bucketName, sessions]) => (
                sessions.length > 0 && (
                  <div key={bucketName}>
                    <h3 className={`px-3 py-2 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {bucketName}
                    </h3>
                    <ul className="divide-y divide-gray-200">
                      {sessions.map((session) => (
                        <li 
                          key={session.session_id}
                          onClick={() => onSelectSession(session.session_id)}
                          className={`p-3 hover:bg-opacity-10 cursor-pointer relative group ${currentSessionId === session.session_id ? (isDarkMode ? 'bg-blue-600/20' : 'bg-blue-50') : ''} ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="truncate flex-1">
                              <div className="font-medium truncate">{getSessionPreview(session)}</div>
                            </div>
                            <div className="flex items-center">
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} group-hover:hidden`}>
                                {getRelativeTime(session.last_updated)}
                              </span>
                              <button
                                onClick={(e) => deleteSession(session.session_id, e)}
                                className={`p-1 rounded hidden group-hover:block ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-500'}`}
                                aria-label="Delete session"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      )}
        
        {/* Login/Logout Button */}
        <div className={`p-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button 
            onClick={handleLogout}
            className={`w-full flex items-center justify-center gap-2 p-2 rounded ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0">
              <path fillRule="evenodd" clipRule="evenodd" d="M6 4C5.44772 4 5 4.44772 5 5V19C5 19.5523 5.44772 20 6 20H10C10.5523 20 11 20.4477 11 21C11 21.5523 10.5523 22 10 22H6C4.34315 22 3 20.6569 3 19V5C3 3.34315 4.34315 2 6 2H10C10.5523 2 11 2.44772 11 3C11 3.55228 10.5523 4 10 4H6ZM15.2929 7.29289C15.6834 6.90237 16.3166 6.90237 16.7071 7.29289L20.7071 11.2929C21.0976 11.6834 21.0976 12.3166 20.7071 12.7071L16.7071 16.7071C16.3166 17.0976 15.6834 17.0976 15.2929 16.7071C14.9024 16.3166 14.9024 15.6834 15.2929 15.2929L17.5858 13H11C10.4477 13 10 12.5523 10 12C10 11.4477 10.4477 11 11 11H17.5858L15.2929 8.70711C14.9024 8.31658 14.9024 7.68342 15.2929 7.29289Z" fill="currentColor"/>
            </svg>
            {isAnonymous ? 'Sign In' : 'Logout'}
          </button>
        </div>
        
        {/* Toast Notification */}
        {toast.isVisible && (
          <div className={`fixed bottom-4 left-4 p-4 rounded-lg shadow-lg ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
            {toast.message}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default HistoryPanel;